import { c as _c } from "react/compiler-runtime";
import { Spinner } from "@/components/dotLoader/DotLoader";
import MyStreamActionTooltip from "@/components/brain/my-stream/MyStreamActionTooltip";
import { AtSymbolIcon, BellSlashIcon } from "@heroicons/react/24/outline";
import type { WaveNotificationSettingsState } from "./useWaveNotificationSettings";
interface WaveNotificationPreferenceButtonsProps {
  readonly waveId: string;
  readonly settings: WaveNotificationSettingsState;
  readonly compact?: boolean | undefined;
}
const getButtonStyle = (active: boolean) => {
  return active ? "tw-bg-primary-400/10 tw-border-primary-400/30 tw-text-primary-400 desktop-hover:tw-hover:tw-bg-primary-400/20" : "tw-text-iron-400 desktop-hover:hover:tw-text-iron-300 tw-bg-transparent tw-border-iron-700";
};
function getAllDropsButtonStyle(settings: WaveNotificationSettingsState) {
  const buttonStyle = getButtonStyle(settings.allDropsEnabled);
  return settings.disableAllDropsSelection && !settings.allDropsEnabled ? `${buttonStyle} tw-cursor-not-allowed` : buttonStyle;
}
function AllDropsIcon(t0) {
  const $ = _c(3);
  const {
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 1 1 0-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 0 1-1.44-4.282m3.102.069a18.03 18.03 0 0 1-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 0 1 8.835 2.535M10.34 6.66a23.847 23.847 0 0 0 8.835-2.535m0 0A23.74 23.74 0 0 0 18.795 3m.38 1.125a23.91 23.91 0 0 1 1.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 0 0 1.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 0 1 0 3.46" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className) {
    t2 = <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className={className}>{t1}</svg>;
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export default function WaveNotificationPreferenceButtons(t0) {
  const $ = _c(37);
  const {
    waveId,
    settings,
    compact: t1
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const allDropsSelectionDisabled = settings.disableAllDropsSelection && !settings.allDropsEnabled;
  const tooltipId = `wave-notification-actions-${waveId}`;
  const allDropsDisabledDescriptionId = `${tooltipId}-all-drops-disabled-description`;
  const allDropsButtonSizeClass = compact ? "tw-size-9 tw-p-0" : "tw-h-10 tw-w-full tw-px-2.5 tw-py-2 lg:tw-h-9";
  const allGroupButtonSizeClass = compact ? "tw-h-9 tw-min-w-9 tw-gap-0 tw-px-2" : "tw-h-10 tw-w-full tw-px-2.5 tw-py-2 lg:tw-h-9";
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <AllDropsIcon className="tw-size-4 tw-flex-shrink-0" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let allDropsButtonContent = t2;
  if (settings.loadingTarget === "all-drops") {
    let t3;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Spinner dimension={12} />;
      $[1] = t3;
    } else {
      t3 = $[1];
    }
    allDropsButtonContent = t3;
  } else {
    if (allDropsSelectionDisabled) {
      let t3;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = <BellSlashIcon className="tw-size-4 tw-flex-shrink-0" />;
        $[2] = t3;
      } else {
        t3 = $[2];
      }
      allDropsButtonContent = t3;
    }
  }
  const t3 = settings.loading;
  const t4 = allDropsSelectionDisabled || undefined;
  const t5 = allDropsSelectionDisabled ? allDropsDisabledDescriptionId : undefined;
  const t6 = settings.allDropsTooltip;
  const t7 = allDropsSelectionDisabled ? undefined : settings.onAllDropsNotificationsClick;
  let t8;
  if ($[3] !== settings) {
    t8 = getAllDropsButtonStyle(settings);
    $[3] = settings;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  const t9 = `tw-flex ${allDropsButtonSizeClass} tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-transition tw-duration-300 tw-ease-out ${t8}`;
  let t10;
  if ($[5] !== allDropsDisabledDescriptionId || $[6] !== allDropsSelectionDisabled || $[7] !== settings.allDropsTooltip) {
    t10 = allDropsSelectionDisabled && <span id={allDropsDisabledDescriptionId} className="tw-sr-only">{settings.allDropsTooltip}</span>;
    $[5] = allDropsDisabledDescriptionId;
    $[6] = allDropsSelectionDisabled;
    $[7] = settings.allDropsTooltip;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  let t11;
  if ($[9] !== allDropsButtonContent || $[10] !== settings.allDropsTooltip || $[11] !== settings.loading || $[12] !== t10 || $[13] !== t4 || $[14] !== t5 || $[15] !== t7 || $[16] !== t9 || $[17] !== tooltipId) {
    t11 = <button type="button" disabled={t3} aria-disabled={t4} aria-describedby={t5} data-tooltip-id={tooltipId} data-tooltip-content={t6} onClick={t7} className={t9} aria-label="Receive all drop notifications">{allDropsButtonContent}{t10}</button>;
    $[9] = allDropsButtonContent;
    $[10] = settings.allDropsTooltip;
    $[11] = settings.loading;
    $[12] = t10;
    $[13] = t4;
    $[14] = t5;
    $[15] = t7;
    $[16] = t9;
    $[17] = tooltipId;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const allDropsButton = t11;
  const t12 = compact ? "tw-flex tw-items-center tw-gap-x-1.5 tw-text-xs" : "tw-grid tw-w-full tw-grid-cols-2 tw-gap-x-1.5 tw-text-xs";
  const t13 = settings.loading;
  const t14 = settings.onAllGroupNotificationsClick;
  const t15 = settings.allGroupTooltip;
  let t16;
  if ($[19] !== settings.allGroupNotificationsEnabled) {
    t16 = getButtonStyle(settings.allGroupNotificationsEnabled);
    $[19] = settings.allGroupNotificationsEnabled;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  const t17 = `tw-flex ${allGroupButtonSizeClass} tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-font-semibold tw-transition tw-duration-300 tw-ease-out ${t16}`;
  let t18;
  if ($[21] !== settings.loadingTarget) {
    t18 = settings.loadingTarget === "all-group" ? <Spinner dimension={12} /> : <><AtSymbolIcon className="tw-size-4 tw-flex-shrink-0" /><span className="-tw-ml-0.5">ALL</span></>;
    $[21] = settings.loadingTarget;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  let t19;
  if ($[23] !== settings.allGroupTooltip || $[24] !== settings.loading || $[25] !== settings.onAllGroupNotificationsClick || $[26] !== t17 || $[27] !== t18 || $[28] !== tooltipId) {
    t19 = <button disabled={t13} onClick={t14} data-tooltip-id={tooltipId} data-tooltip-content={t15} className={t17} aria-label="Receive ALL mention notifications">{t18}</button>;
    $[23] = settings.allGroupTooltip;
    $[24] = settings.loading;
    $[25] = settings.onAllGroupNotificationsClick;
    $[26] = t17;
    $[27] = t18;
    $[28] = tooltipId;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] !== tooltipId) {
    t20 = <MyStreamActionTooltip id={tooltipId} />;
    $[30] = tooltipId;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== allDropsButton || $[33] !== t12 || $[34] !== t19 || $[35] !== t20) {
    t21 = <div className={t12}>{t19}{allDropsButton}{t20}</div>;
    $[32] = allDropsButton;
    $[33] = t12;
    $[34] = t19;
    $[35] = t20;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  return t21;
}